import { Avatar, AvatarProps, Badge, Stack } from "@mui/material"
import classNames from "classnames"

import { Cropper, CircleStencil, CropperRef } from "react-advanced-cropper"
import { useTranslation } from "react-i18next"

import "./profileUploader.scss"
import { useEffect, useRef, useState } from "react"
import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import { acceptedImageExtensions } from "@/constants/image"

import "react-advanced-cropper/dist/style.css"
import { ImageExtension } from "@/types/image"
import Icon from "@/components/icon/Icon"
import { DefaultProfile } from "@/components/icon/svg"
import { colorTokens } from "@/components/colorTokens"

type ProfileUploaderProps = {
  imgUrl?: string
  onSave?: (file?: Blob) => void
  onRemove?: () => void
  width: number
  height: number
} & AvatarProps

export default function ProfileUploader({ imgUrl, width, height, ...props }: ProfileUploaderProps) {
  const [open, setOpen] = useState(false)
  const [localImgUrl, setLocalImgUrl] = useState<string | undefined>(imgUrl)
  const [inputImgUrl, setInputImgUrl] = useState<string | undefined>(undefined)
  const [isRemoved, setIsRemoved] = useState(false)

  const [acceptedFilesInfo, setAcceptedFilesInfo] = useState<ImageExtension>()

  const fileUploaderRef = useRef<HTMLInputElement>(null)

  const cropperRef = useRef<CropperRef>(null)
  const { t } = useTranslation("")

  const removeImage = () => {
    setIsRemoved(true)
    setLocalImgUrl(undefined)
    setInputImgUrl(undefined)
  }

  const resetImg = () => {
    setInputImgUrl(undefined)
    setIsRemoved(false)

    if (!imgUrl) {
      setLocalImgUrl(undefined)
    } else {
      setLocalImgUrl(imgUrl)
    }

    setOpen(false)
  }

  const openFileSelector = () => {
    if (fileUploaderRef.current) {
      fileUploaderRef.current.click()

      return
    }

    const fileSelector = document.createElement("input")
    fileSelector.type = "file"
    fileSelector.accept = acceptedFilesInfo?.MIMEType || "image/*"
    fileSelector.addEventListener("change", (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) {
        return
      }

      setLocalImgUrl(undefined)
      const reader = new FileReader()
      reader.onloadend = () => {
        setInputImgUrl(reader.result as string)
      }
      reader.readAsDataURL(file)
      fileSelector.remove()
    })

    fileSelector.click()
  }

  const onClickSave = () => {
    if (isRemoved) {
      props.onRemove?.()
      setOpen(false)
      setInputImgUrl(undefined)
      setIsRemoved(false)

      return
    }

    if (cropperRef.current && cropperRef.current.getCanvas()) {
      const canvas = cropperRef.current.getCanvas()

      const croppedDataUrl = canvas?.toDataURL()
      if (croppedDataUrl) {
        setLocalImgUrl(croppedDataUrl)
      }

      canvas?.toBlob((blob) => {
        if (blob) {
          props.onSave?.(blob)
        }
      }, "image/png")
    }

    setOpen(false)
    setInputImgUrl(undefined)
  }

  useEffect(() => {
    const extension = acceptedImageExtensions.map(({ extension }) => extension).join(", ")
    const MIMEType = acceptedImageExtensions.map(({ MIMEType }) => MIMEType).join(", ")

    setAcceptedFilesInfo({
      extension,
      MIMEType,
    })
  }, [])

  useEffect(() => {
    setLocalImgUrl(imgUrl)
  }, [imgUrl])

  return (
    <div className={classNames("ProfileUploaderCont", props.className)}>
      <Stack direction="row" spacing={2}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <div className="ic-camera cursor-pointer">
              <Icon size={16} type="camera"></Icon>
            </div>
          }
          onClick={() => setOpen(true)}
          sx={{ height, width }}>
          <Avatar
            className="cursor-pointer"
            src={localImgUrl || "/images/person.png"}
            onClick={() => setOpen(true)}
            sx={{
              height: width,
              width: width,
              borderRadius: width,
              backgroundColor: colorTokens.icon.disabled,
            }}>
            {!localImgUrl && <DefaultProfile />}
          </Avatar>
        </Badge>
      </Stack>
      <Dialog
        className="ProfileUploaderModal"
        open={open}
        onClose={resetImg}
        maxWidth="600px"
        title={t("editProfilePhoto")}
        footer={
          <>
            <BasicButton type="default-outlined" size="md" onClick={resetImg}>
              <p>{t("button.cancel")}</p>
            </BasicButton>
            <BasicButton
              disabled={!inputImgUrl && !isRemoved}
              type="primary-contained"
              size="md"
              onClick={onClickSave}>
              {t("button.save")}
            </BasicButton>
          </>
        }>
        <div className="ProfileUploaderModalBody">
          {localImgUrl && (
            <>
              <Avatar className="avatar" src={localImgUrl} sx={{ height: 360, width: 360 }} />
              <div className="button-cont">
                <BasicButton size="md" type="danger-outlined" onClick={removeImage}>
                  {t("button.remove")}
                </BasicButton>

                <BasicButton size="md" type="default-outlined" onClick={openFileSelector}>
                  {t("messages.uploadPhoto")}
                </BasicButton>
              </div>
            </>
          )}
          {inputImgUrl && (
            <>
              <Cropper
                className="cropper"
                ref={cropperRef}
                src={inputImgUrl}
                stencilComponent={CircleStencil}
                stencilProps={{
                  aspectRatio: 1,
                  grid: true,
                }}
              />
              <div className="button-cont">
                <BasicButton size="md" type="danger-outlined" onClick={removeImage}>
                  {t("button.remove")}
                </BasicButton>
                <BasicButton size="md" type="default-outlined" onClick={openFileSelector}>
                  {t("messages.uploadPhoto")}
                </BasicButton>
              </div>
            </>
          )}
          {!localImgUrl && !inputImgUrl && (
            <>
              <label className="fileUploader" htmlFor="fileUploader">
                <div className="ic-add">
                  <Icon type="image-add"></Icon>
                </div>

                <dl>
                  <dt>{t("messages.uploadPhoto")}</dt>
                  <dd>{acceptedFilesInfo?.extension}</dd>
                </dl>

                <input
                  id="fileUploader"
                  ref={fileUploaderRef}
                  type="file"
                  accept={acceptedFilesInfo?.MIMEType || "image/*"}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) {
                      return
                    }

                    const reader = new FileReader()
                    reader.onloadend = () => {
                      setInputImgUrl(reader.result as string)
                    }
                    reader.readAsDataURL(file)
                  }}
                />
              </label>
            </>
          )}
        </div>
      </Dialog>
    </div>
  )
}
